import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRecentArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));
  const relatedArticles = getRecentArticles(3).filter((a) => a.id !== Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-[400px] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const catName = cat ? cat.name : article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '柠檬导航',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.lemonnav.cn/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.lemonnav.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 柠檬导航`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},柠檬导航,网址导航,网站推荐`}
        canonicalUrl={`https://www.lemonnav.cn/article/${article.id}`}
        ogImage={article.imageUrl}
        ogType="article"
        schemaData={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: catName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article header */}
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 font-heading leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-foreground-500 mb-8">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>
        </div>

        {/* Featured image */}
        <div className="max-w-3xl mx-auto px-4 md:px-6 mb-10">
          <div className="rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </div>
        </div>

        {/* Article content */}
        <article className="max-w-3xl mx-auto px-4 md:px-6 pb-16">
          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </article>

        {/* Related articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h3 className="text-lg md:text-xl font-bold text-foreground-900 font-heading mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{ra.date} · {ra.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}